import { useIsMutating, useQuery, useQueryClient } from "@-/com.npmjs.tanstack__react-query";
import { TextInput, View } from "@-/com.npmjs.react-native";
import {
  getSimplifiedLevels,
  getTraditionalLevels,
} from "@-/dev.improve.haniverse.assets.graphemes.levels";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import {
  findCurrentLevelIndex,
  SET_CURRENT_LEVEL_MUTATION_KEY,
  useSetCurrentLevel,
} from "@-/dev.improve.haniverse.lib.cards.deck.level";
import { useStudiedGraphemesQueryOptions } from "@-/dev.improve.haniverse.lib.cards.user-card.grapheme.meaning.read";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import { requestLearnReset } from "@-/dev.improve.haniverse.routes.nav.app.learn";
import { useState } from "react";
import { AdminPanelButton, AdminPanelSection, AdminPanelStatus } from "#admin-panel-section";

/** How many of the target level's characters preview beside the picker. */
const PREVIEW_CHARACTER_COUNT = 16;

export function AdminPanelLevelSection() {
  // The script the Learn tab walks (and `useSetCurrentLevel` enrolls in).
  const { hanziScript } = useActorUser();
  const levels = hanziScript === "traditional" ? getTraditionalLevels() : getSimplifiedLevels();
  const queryClient = useQueryClient();
  const { data: studiedGraphemes } = useQuery({
    ...useStudiedGraphemesQueryOptions(),
    throwOnError: false,
  });
  const currentLevelIndex =
    studiedGraphemes === undefined ? null : findCurrentLevelIndex(levels, studiedGraphemes);
  const { mutation, progress } = useSetCurrentLevel({
    onSuccess: () => {
      // A cancelled or frozen Learn read keeps its old position; re-resolve every dependent read,
      // then send the Learn tab back to its summary (as Reset onboarding does). Runs even if the
      // panel was closed mid-walk.
      void Promise.all(
        [
          ["actorQuery", "userSlide"],
          ["completedSlideKeys"],
          ["nextLesson"],
          ["learnSummaryPage"],
        ].map((queryKey) => queryClient.invalidateQueries({ queryKey, refetchType: "all" })),
      ).then(() => {
        requestLearnReset();
      });
    },
  });
  // A walk started before the panel was last closed keeps running; never start a second one.
  const isAnyWalkRunning = useIsMutating({ mutationKey: SET_CURRENT_LEVEL_MUTATION_KEY }) > 0;
  // `null` follows the current level until the picker is touched.
  const [levelNumberText, setLevelNumberText] = useState<string | null>(null);

  const levelCount = levels.length;
  const levelNumberInput =
    levelNumberText ?? (currentLevelIndex === null ? "" : String(currentLevelIndex + 1));
  const levelNumber = Number.parseInt(levelNumberInput, 10);
  const targetLevel = Number.isInteger(levelNumber)
    ? levels.find((level) => level.index === levelNumber - 1)
    : undefined;
  const stepLevel = (delta: number) => {
    const from = Number.isInteger(levelNumber) ? levelNumber : 1;
    setLevelNumberText(String(Math.min(levelCount, Math.max(1, from + delta))));
  };

  const status = (() => {
    switch (mutation.status) {
      case "pending": {
        return progress === null
          ? "Clearing the deck…"
          : `Enrolling lessons ${progress.done}/${progress.total}…`;
      }
      case "error": {
        return mutation.error.message;
      }
      case "success": {
        const skipped =
          mutation.data.skippedCount === 0 ? "" : `, skipped ${mutation.data.skippedCount}`;
        return `Now at Level ${mutation.variables.levelIndex + 1} · enrolled ${mutation.data.enrolledCount} lessons${skipped}`;
      }
      default: {
        return isAnyWalkRunning ? "An earlier level change is still running…" : null;
      }
    }
  })();

  return (
    <AdminPanelSection
      description="Every lesson before the level becomes studied (reviews due over the next 30 days) and its slides seen; everything from it on is unstudied. Wipes review history."
      title="Current level"
    >
      <Text className="text-sm text-muted-foreground" testID="admin-panel-current-level">
        {currentLevelIndex === null
          ? studiedGraphemes === undefined
            ? "Reading progress…"
            : "Every level is complete"
          : `Currently on Level ${currentLevelIndex + 1} of ${levelCount}`}
      </Text>
      <View className="flex-row items-center gap-2">
        <AdminPanelButton
          accessibilityLabel="Previous level"
          disabled={!Number.isInteger(levelNumber) || levelNumber <= 1}
          onPress={() => stepLevel(-1)}
          title="−"
        />
        <TextInput
          accessibilityLabel="Level number"
          className="h-9 w-20 rounded-xs border border-border bg-background px-2 text-center font-sans text-base text-foreground"
          inputMode="numeric"
          keyboardType="number-pad"
          maxLength={4}
          onChangeText={setLevelNumberText}
          placeholder="Level"
          returnKeyType="done"
          selectTextOnFocus
          testID="admin-panel-level-input"
          value={levelNumberInput}
        />
        <AdminPanelButton
          accessibilityLabel="Next level"
          disabled={Number.isInteger(levelNumber) && levelNumber >= levelCount}
          onPress={() => stepLevel(1)}
          title="+"
        />
        <Text className="flex-1 text-sm text-foreground" numberOfLines={1}>
          {targetLevel === undefined
            ? `Pick 1–${levelCount}`
            : targetLevel.characters.slice(0, PREVIEW_CHARACTER_COUNT).join("") +
              (targetLevel.characters.length > PREVIEW_CHARACTER_COUNT ? "…" : "")}
        </Text>
      </View>
      <View className="flex-row items-center gap-3">
        <AdminPanelButton
          disabled={targetLevel === undefined || isAnyWalkRunning}
          onPress={() => {
            if (targetLevel === undefined) {
              return;
            }
            mutation.mutate(
              { levelIndex: targetLevel.index },
              {
                onSuccess: () => {
                  setLevelNumberText(null);
                },
              },
            );
          }}
          title={
            targetLevel === undefined ? "Set level" : `Start at Level ${targetLevel.index + 1}`
          }
          variant="primary"
        />
        {status === null ? null : (
          <View className="flex-1">
            <AdminPanelStatus isError={mutation.isError} text={status} />
          </View>
        )}
      </View>
    </AdminPanelSection>
  );
}
